<template>
  <Transition name="action-panel">
    <div
      v-if="visible"
      class="fixed bottom-12 left-1/2 transform max-h-16 -translate-x-1/2 bg-surface-light text-font-primary rounded-lg shadow-xl py-5 px-6 flex items-center gap-3 z-30 text-sm will-change-transform"
    >
      <!-- Selected count -->
      <slot name="selection-count">
        <label class="whitespace-nowrap cursor-pointer flex items-center group">
          <span class="sr-only">Unselect All</span>
          <div
            class="whitespace-nowrap flex items-center pointer-events-auto"
            :data-test-id="generateDataTestId(dataTestId, 'selection-count')"
            @click="$emit('unselect-all')"
          >
            <AM3Checkbox
              :model-value="true"
              class="mr-4 font-medium pointer-events-none"
            />
            {{ selectionText }}
          </div>
        </label>
      </slot>

      <!-- Only show divider if we have both selection count and actions -->
      <div
        v-if="hasSelectionSlot && hasActionsSlot"
        class="h-6 w-px bg-surfaces-disabled"
      />

      <!-- Actions -->
      <slot name="actions">
        <template
          v-for="(action, index) in visibleActions"
          :key="action.event || action.label"
        >
          <!-- Action button or dropdown trigger -->
          <div class="relative">
            <button
              :id="'floating-action-panel-subactions-'+action.label"
              class="flex items-center gap-2 hover:bg-surfaces-neutral outline-1 outline rounded-md outline-transparent active:bg-brand-light focus:bg-brand-light transition-colors duration-200 px-2 py-1"
              :data-test-id="generateDataTestId(dataTestId, 'action', action.label)"
              @click="action.subactions ? toggleSubactions(action) : $emit('action', action.event)"
            >
              <AM3Icon
                :type="action.icon"
                size="sm"
                color="currentColor"
              />
              <span class="text-nowrap font-medium">{{ action.label }}</span>
            </button>
            <!-- Dropdown for subactions -->
            <AM3DropdownPanel
              v-if="action.subactions"
              v-model="openActionDropdowns[action.label]"
              position="top"
              :target="'floating-action-panel-subactions-'+action.label"
            >
              <div class="py-1">
                <button
                  v-for="subaction in action.subactions"
                  :key="subaction.event"
                  class="w-full text-left px-4 py-2 hover:bg-surfaces-neutral outline-1 outline outline-transparent active:bg-brand-light focus:bg-brand-light transition-colors duration-200"
                  :data-test-id="generateDataTestId(dataTestId, 'subaction', `${action.label}-${subaction.event}`)"
                  @click="handleSubaction(subaction.event)"
                >
                  {{ subaction.label }}
                </button>
              </div>
            </AM3DropdownPanel>
          </div>

          <!-- Divider after each action except the last -->
          <div
            v-if="visibleActions && index < visibleActions.length - 1"
            class="h-6 w-px bg-surfaces-disabled"
          />
        </template>
      </slot>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed, ref, useSlots } from 'vue'
import AM3Checkbox from '@/components/am3-generic/inputs/selection/AM3Checkbox.vue'
import AM3Icon from '@/components/am3-generic/base/icon/AM3Icon.vue'
import AM3DropdownPanel from '@/components/am3-generic/base/AM3DropdownPanel.vue'
import { generateDataTestId } from '@/components/am3-generic/core/dataTestId'

// Props
const props = defineProps<{
  visible: boolean
  actions?: FloatingActionPanelAction[]
  selectedCount?: number
  totalCount?: number
  selectAllValue?: boolean
  itemVocabulary?: [string, string] // [singular, plural]
  dataTestId: string
  selection?: any[] // The current selection to pass to hide functions
}>()

// Provide default values
const defaultProps = {
  itemVocabulary: ['item', 'items'],
  dataTestId: 'floating-action-panel',
  selectAllValue: true,
}

// Handle events
const emit = defineEmits<{
  'action': [event: string]
  'unselect-all': []
}>()

// Refs for component state
const openActionDropdowns = ref<Record<string, boolean>>({})

// Detect if slots are provided
const slots = useSlots()
const hasSelectionSlot = computed(() => !!slots['selection-count'])
const hasActionsSlot = computed(() => !!slots.actions || (props.actions && props.actions.length > 0))

// Computed properties
const selectionText = computed(() => {
  const count = props.selectedCount ?? 0
  const vocabulary = props.itemVocabulary ?? defaultProps.itemVocabulary
  const term = count === 1 ? vocabulary[0] : vocabulary[1]

  if (props.selectAllValue && props.totalCount) {
    return `${props.totalCount.toLocaleString()} ${term} selected`
  }

  return `${count.toLocaleString()} ${term} selected`
})

// Filter actions based on hide function
const visibleActions = computed(() => {
  if (!props.actions) return []

  return props.actions.filter((action) => {
    // If hide function exists, call it with the selection
    if (action.hide && typeof action.hide === 'function') {
      return !action.hide(props.selection || [])
    }
    // If no hide function, show the action
    return true
  })
})

// Methods
function toggleSubactions(action: FloatingActionPanelAction) {
  openActionDropdowns.value[action.label] = !openActionDropdowns.value[action.label]
}

function handleSubaction(eventName: string) {
  openActionDropdowns.value = {}
  emit('action', eventName)
}
</script>

<script lang="ts">
export interface FloatingActionPanelAction {
  label: string
  icon: string
  event: string
  hide?: (selection: any[]) => boolean
  subactions?: {
    label: string
    event: string
  }[]
}
</script>

<style scoped>
.action-panel-enter-active,
.action-panel-leave-active {
  transition: transform 0.125s ease-in-out, opacity 0.125s ease-in-out;
}

.action-panel-enter-from,
.action-panel-leave-to {
  transform: translate(-50%, 100%);
  opacity: 0;
}

.action-panel-enter-to,
.action-panel-leave-from {
  transform: translate(-50%, 0);
  opacity: 1;
}
</style>
